import { assignedAgencies } from '../auth/agencyAccess';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  BriefcaseBusiness,
  LayoutDashboard,
  RefreshCw,
} from 'lucide-react';

import { Button, Skeleton } from '@shigo-ui/ui';

import { AuthPanel, AuthStep } from '../auth/components/AuthLayout';
import {
  canOpenMainDashboard,
  canOpenPublisherDashboard,
  publisherDashboardPath,
} from '../auth/dashboardDestination';
import { dashboardT } from '../../lib/messages';
import { useShigoSession } from '../../lib/useShigoSession';
import { useAssignedPublishersQuery } from './hooks';

const dashboardOptionClassName =
  'border-border bg-card hover:border-foreground/20 hover:bg-muted focus-visible:ring-ring/40 group flex min-h-[72px] min-w-0 w-full cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium shadow-xs transition-colors focus-visible:ring-2 focus-visible:outline-none';

export function DashboardSelectionStep() {
  const { clearSession, session } = useShigoSession();
  const agencies = assignedAgencies(session?.user);
  const canOpenPublishers = canOpenPublisherDashboard(session?.user);
  const publishersQuery = useAssignedPublishersQuery(canOpenPublishers);
  const canOpenMain = canOpenMainDashboard(session?.user);
  const publisherLandingPath = publisherDashboardPath(session?.user.permissions ?? []);
  const loadingRowCount = (canOpenMain ? 1 : 0) + (session?.user.publisherIds.length ?? 0);

  return (
    <AuthPanel
      description={dashboardT('dashboardSelection.description')}
      headerAction={
        <Button
          className="text-muted-foreground -ml-3"
          onClick={clearSession}
          size="sm"
          type="button"
          variant="ghost"
        >
          <ArrowLeft className="h-4 w-4" />
          {dashboardT('common.back')}
        </Button>
      }
      step={AuthStep.DashboardSelection}
      title={dashboardT('dashboardSelection.title')}
    >
      <div className="grid gap-5" data-testid="dashboard-selection-card">
        {publishersQuery.isLoading ? (
          <div
            aria-label={dashboardT('dashboardSelection.loading')}
            className="contents"
            data-testid="dashboard-selection-loading"
            role="status"
          >
            {Array.from({ length: loadingRowCount }, (_, index) => (
              <Skeleton className="h-[72px] w-full rounded-xl" key={index} />
            ))}
          </div>
        ) : null}
        {!publishersQuery.isLoading && canOpenMain ? (
          <Link className={dashboardOptionClassName} to="/">
            <span className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
              <LayoutDashboard className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">{dashboardT('dashboardSelection.main')}</span>
            <ArrowRight
              className="text-muted-foreground h-4 w-4 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
        ) : null}
        {canOpenPublishers &&
        !publishersQuery.isLoading &&
        (publishersQuery.isError || (publishersQuery.data?.content.length ?? 0) > 0) ? (
          <section
            aria-label={dashboardT('dashboardSelection.publisherTitle')}
            className="min-w-0 space-y-2.5"
          >
            <h2 className="text-muted-foreground text-xs font-semibold">
              {dashboardT('dashboardSelection.publisherTitle')}
            </h2>
            {publishersQuery.isError ? (
              <div className="border-destructive/40 bg-destructive/5 space-y-3 rounded-xl border p-4 text-center">
                <p className="text-destructive text-sm">{dashboardT('dashboardSelection.error')}</p>
                <Button onClick={() => void publishersQuery.refetch()} size="sm" variant="outline">
                  <RefreshCw className="h-4 w-4" />
                  {dashboardT('common.retry')}
                </Button>
              </div>
            ) : null}
            {publishersQuery.data?.content.map((publisher) => (
              <Link
                className={dashboardOptionClassName}
                key={publisher.id}
                params={{ publisherId: publisher.id }}
                to={publisherLandingPath}
              >
                <span className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                  <Building2 className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1 truncate">{publisher.name}</span>
                <ArrowRight
                  className="text-muted-foreground h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </Link>
            ))}
          </section>
        ) : null}
        {agencies.length > 0 ? (
          <section
            aria-label={dashboardT('dashboardSelection.agencyTitle')}
            className="min-w-0 space-y-2.5"
          >
            <h2 className="text-muted-foreground text-xs font-semibold">
              {dashboardT('dashboardSelection.agencyTitle')}
            </h2>
            {agencies.map((agency) => (
              <Link
                key={agency.id}
                to="/a/$agencyId"
                params={{ agencyId: agency.id }}
                className={dashboardOptionClassName}
              >
                <span className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                  <BriefcaseBusiness className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1 truncate">{agency.name}</span>
                <ArrowRight className="text-muted-foreground h-4 w-4" aria-hidden="true" />
              </Link>
            ))}
          </section>
        ) : null}
      </div>
    </AuthPanel>
  );
}
